import { useEffect, useMemo, useState } from "react";
import { getFeedMapping, type FeedFinding, type MappingRow } from "./actions";
import { downloadCsv } from "./csv";

// Feed mapping: the legend of how each vendor's columns become the catalog fields WooCommerce (W), Google Merchant Center (M) and SEO (S)
// need, so nothing is mapped silently. Below it, the review items measured in the feeds: places where a mapping may be wrong or
// misleading and needs confirming, with what to ask the vendor. INTERNAL. Export downloads a CSV only and sends nothing to anyone.

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const toCsv = (head: string[], lines: unknown[][]) => [head, ...lines].map((l) => l.map(csvCell).join(",")).join("\r\n");
const STATE_LABEL: Record<MappingRow["state"], string> = { captured: "captured", not_captured: "not captured yet", absent: "not in the feed" };

export function FeedMappingPanel() {
  const [legend, setLegend] = useState<MappingRow[]>([]);
  const [review, setReview] = useState<FeedFinding[]>([]);
  const [runDate, setRunDate] = useState("");
  const [feed, setFeed] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFeedMapping().then((r) => {
      setLoading(false);
      if (r.ok) { setLegend(r.data.legend); setReview(r.data.review); setRunDate(r.data.runDate); } else setError(r.error);
    });
  }, []);

  const feeds = useMemo(() => {
    const seen = new Map<string, string>();
    legend.forEach((m) => seen.set(m.feed, m.feedName));
    return Array.from(seen, ([key, name]) => ({ key, name }));
  }, [legend]);
  const shownLegend = legend.filter((m) => (!feed || m.feed === feed) && (!openOnly || m.confirm || m.state !== "captured"));
  const shownReview = review.filter((r) => !feed || r.vendor === feed);
  const feedName = (k: string) => feeds.find((f) => f.key === k)?.name ?? k;

  return (
    <div className="sheet-page">
      <div className="sheet-top">
        <div className="sheet-title">
          <h1>Feed mapping</h1>
          <span className="meta">
            {loading ? "Loading…" : `${shownLegend.length} mappings, ${shownReview.length} review item${shownReview.length === 1 ? "" : "s"}${runDate ? ` · measured ${runDate}` : ""}`}.
            W = WooCommerce, M = Merchant Center, S = SEO. Exports download a CSV only and send nothing to anyone.
          </span>
        </div>
        <div className="tabs">
          <button className={!feed ? "active" : ""} onClick={() => setFeed("")}>All feeds</button>
          {feeds.map((f) => <button key={f.key} className={feed === f.key ? "active" : ""} onClick={() => setFeed(f.key)}>{f.name}</button>)}
          <label className="meta" style={{ marginLeft: 16 }}>
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /> only rows that need confirming or are not captured
          </label>
        </div>
        {error && <div className="panel error sheet-error">{error}</div>}
        <div className="sheet-actions">
          <button type="button" className="sheet-export" disabled={!shownLegend.length}
                  onClick={() => downloadCsv(`hec-feed-mapping${feed ? "-" + feed : ""}.csv`, toCsv(
                    ["feed", "catalog field", "used by", "feed column", "what we do", "state", "to confirm"],
                    shownLegend.map((m) => [m.feedName, m.field, m.usedBy, m.source, m.transform, STATE_LABEL[m.state], m.confirm])))}>
            Export mapping ({shownLegend.length})
          </button>
          <button type="button" className="sheet-export" disabled={!shownReview.length}
                  onClick={() => downloadCsv(`hec-mapping-review${feed ? "-" + feed : ""}-${runDate || "latest"}.csv`, toCsv(
                    ["feed", "file", "severity", "item", "what we found", "rows affected", "of total rows", "examples", "what to confirm or ask", "measured"],
                    shownReview.map((r) => [feedName(r.vendor), r.file, r.severity, r.title, r.detail, r.count, r.total, (r.examples || []).join("; "), r.request, r.run_date])))}>
            Export review items ({shownReview.length})
          </button>
        </div>
      </div>

      <div className="sheet-scroll">
        <h2 className="map-h">Review items <span className="meta">measured in the live feed files</span></h2>
        <table className="manual-products sheet-table feed-table">
          <thead><tr><th>Feed</th><th>Severity</th><th>Item</th><th>How many</th><th>Examples</th><th>What we found</th><th>What to confirm or ask</th></tr></thead>
          <tbody>
            {shownReview.map((r) => (
              <tr key={r.docId}>
                <td>{feedName(r.vendor)}</td>
                <td><span className={`sev sev-${r.severity}`}>{r.severity}</span></td>
                <td className="wrap">{r.title}</td>
                <td className="num">{r.count.toLocaleString()} of {r.total.toLocaleString()}</td>
                <td className="mono wrap">{(r.examples || []).join(", ")}</td>
                <td className="wrap">{r.detail}</td>
                <td className="wrap">{r.request}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shownReview.length === 0 && !loading && !error && (
          <p className="meta" style={{ padding: 16 }}>No review items stored for this environment yet. Run build_mapping_review.py with --write to measure them.</p>
        )}

        <h2 className="map-h">Legend <span className="meta">how each feed's columns map to the catalog fields</span></h2>
        <table className="manual-products sheet-table feed-table">
          <thead><tr><th>Feed</th><th>Catalog field</th><th>Used by</th><th>Feed column</th><th>What we do</th><th>State</th><th>To confirm</th></tr></thead>
          <tbody>
            {shownLegend.map((m) => (
              <tr key={`${m.feed}-${m.field}`}>
                <td>{m.feedName}</td>
                <td className="mono">{m.field}</td>
                <td>{m.usedBy}</td>
                <td className="mono wrap">{m.source}</td>
                <td className="wrap">{m.transform}</td>
                <td><span className={`map-state map-${m.state}`}>{STATE_LABEL[m.state]}</span></td>
                <td className="wrap">{m.confirm}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
